import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, ArrowUpRight, ChevronDown } from "lucide-react";
import useLenis from "../hooks/useLenis";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import blogs from "../data/blogs.json";

function formatDate(dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

function FAQAccordion({ items }) {
    const [openIndex, setOpenIndex] = useState(0);

    return (
        <section className="mt-12 border-t border-communiti-line pt-10">
            <h2 className="font-display text-2xl font-bold tracking-tight text-communiti-text">
                FAQs
            </h2>
            <div className="mt-6 divide-y divide-communiti-line border-y border-communiti-line">
                {items.map((item, index) => {
                    const isOpen = openIndex === index;
                    const panelId = `faq-panel-${index}`;
                    const triggerId = `faq-trigger-${index}`;

                    return (
                        <div key={item.question}>
                            <button
                                id={triggerId}
                                type="button"
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => setOpenIndex(isOpen ? -1 : index)}
                                className="flex w-full items-center justify-between gap-6 py-5 text-left"
                            >
                                <span
                                    className="font-display text-base font-bold tracking-tight text-communiti-text sm:text-lg"
                                    dangerouslySetInnerHTML={{ __html: item.question }}
                                />
                                <ChevronDown
                                    className={`h-5 w-5 shrink-0 text-communiti transition-transform duration-300 ${
                                        isOpen ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            <div
                                id={panelId}
                                role="region"
                                aria-labelledby={triggerId}
                                className={`grid transition-[grid-template-rows,opacity] duration-300 ${
                                    isOpen
                                        ? "grid-rows-[1fr] opacity-100"
                                        : "grid-rows-[0fr] opacity-0"
                                }`}
                            >
                                <div className="overflow-hidden">
                                    <div
                                        className="pb-5 pr-10 text-sm leading-relaxed text-communiti-muted sm:text-base"
                                        dangerouslySetInnerHTML={{ __html: item.answer }}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

const BLOG_STYLES = `
    .blog-content h2 {
        font-family: var(--font-display);
        font-size: 1.5rem;
        font-weight: 700;
        letter-spacing: -0.02em;
        line-height: 1.2;
        color: var(--color-fg);
        margin-top: 2.5rem;
        margin-bottom: 1rem;
        scroll-margin-top: 7rem;
    }
    .blog-content h3 {
        font-family: var(--font-display);
        font-size: 1.25rem;
        font-weight: 700;
        letter-spacing: -0.02em;
        line-height: 1.25;
        color: var(--color-fg);
        margin-top: 2rem;
        margin-bottom: 0.75rem;
        scroll-margin-top: 7rem;
    }
    .blog-content p {
        font-family: var(--font-body);
        font-size: 1rem;
        line-height: 1.75;
        color: var(--color-muted);
        margin-top: 1rem;
        margin-bottom: 1rem;
    }
    .blog-content ul, .blog-content ol {
        margin-top: 1rem;
        margin-bottom: 1rem;
        padding-left: 1.5rem;
        color: var(--color-muted);
    }
    .blog-content ul {
        list-style: none;
        padding-left: 0;
    }
    .blog-content li {
        font-family: var(--font-body);
        font-size: 1rem;
        line-height: 1.75;
        margin-bottom: 0.5rem;
    }
    .blog-content ul li {
        position: relative;
        padding-left: 1.9rem;
    }
    .blog-content ul li::before {
        content: "✓";
        position: absolute;
        left: 0;
        top: 0.35rem;
        display: inline-flex;
        width: 1.15rem;
        height: 1.15rem;
        align-items: center;
        justify-content: center;
        border-radius: 9999px;
        background: var(--color-blue);
        color: white;
        font-size: 0.7rem;
        font-weight: 700;
        line-height: 1;
    }
    .blog-content li::marker {
        color: var(--color-blue);
    }
    .blog-content strong {
        font-weight: 600;
        color: var(--color-fg);
    }
    .blog-content a {
        color: var(--color-blue);
        text-decoration: underline;
        text-underline-offset: 3px;
    }
    .blog-content a:hover {
        color: var(--color-blue-soft);
    }
    .blog-content blockquote {
        border-left: 3px solid var(--color-blue);
        padding-left: 1.25rem;
        margin: 1.5rem 0;
        font-style: italic;
        color: var(--color-fg);
    }
    .blog-content .comparison-table-wrap {
        margin: 2rem 0;
        width: 100%;
        overflow: hidden;
        border: 1px solid #000;
    }
    .blog-content .comparison-table {
        width: 100%;
        table-layout: fixed;
        border-collapse: collapse;
        text-align: left;
    }
    .blog-content .comparison-table th {
        width: 50%;
        background: var(--color-surface);
        color: var(--color-fg);
        font-family: var(--font-display);
        font-size: 1.25rem;
        font-weight: 700;
        line-height: 1.15;
        padding: 1.25rem 2rem;
        vertical-align: top;
        border: 1px solid #000;
    }
    .blog-content .comparison-table td {
        width: 50%;
        border: 1px solid #000;
        color: var(--color-fg);
        font-family: var(--font-body);
        font-size: 1rem;
        line-height: 1.75;
        padding: 1rem 2rem;
        vertical-align: top;
        overflow-wrap: anywhere;
    }
    @media (max-width: 640px) {
        .blog-content .comparison-table th {
            font-size: 1.25rem;
            padding: 0.85rem 0.65rem;
        }
        .blog-content .comparison-table td {
            font-size: 1rem;
            line-height: 1.75;
            padding: 0.75rem 0.65rem;
        }
    }
`;

export default function BlogPost() {
    useLenis();
    const { slug } = useParams();
    const blog = blogs.find((b) => b.id === slug);

    const relatedPosts = useMemo(() => {
        if (!blog) return [];
        return blogs
            .filter((b) => b.id !== blog.id && b.category === blog.category)
            .slice(0, 2);
    }, [blog]);

    const articleContent = useMemo(() => {
        if (!blog) return { body: "", faqs: [], toc: [] };

        const faqMarkers = [
            "<h2>FAQ’s</h2>",
            "<p><strong>FAQ’s</strong></p>",
        ];
        const faqMarker = faqMarkers.find((marker) =>
            blog.content.includes(marker)
        );
        const markerIndex = faqMarker ? blog.content.indexOf(faqMarker) : -1;
        const firstQuestionIndex = blog.content.search(/<h3>1\.\s/);
        const faqStart = markerIndex >= 0 ? markerIndex : firstQuestionIndex;
        const rawBody =
            faqStart >= 0 ? blog.content.slice(0, faqStart) : blog.content;
        const faqContent =
            faqStart >= 0
                ? blog.content.slice(
                      faqStart + (markerIndex >= 0 ? faqMarker.length : 0)
                  )
                : "";
        const faqs = [];
        const faqPattern = /<h3>(.*?)<\/h3><p>(.*?)<\/p>/gs;
        let match;

        while ((match = faqPattern.exec(faqContent)) !== null) {
            faqs.push({ question: match[1], answer: match[2] });
        }

        const toc = [];
        const usedIds = {};
        const body = rawBody.replace(
            /<h([23])>(.*?)<\/h\1>/g,
            (fullMatch, level, heading) => {
                const label = heading.replace(/<[^>]*>/g, "");
                const baseId =
                    label
                        .toLowerCase()
                        .replace(/&[^;]+;/g, "")
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/^-|-$/g, "") || "section";
                usedIds[baseId] = (usedIds[baseId] || 0) + 1;
                const id =
                    usedIds[baseId] > 1
                        ? `${baseId}-${usedIds[baseId]}`
                        : baseId;

                toc.push({ id, label, level: Number(level) });
                return `<h${level} id="${id}">${heading}</h${level}>`;
            }
        );

        return { body, faqs, toc };
    }, [blog]);

    useEffect(() => {
        if (!blog) return;
        document.title = `${blog.title} | Commmmuniti Media Marketing`;
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute("content", blog.excerpt);
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [blog]);

    if (!blog) {
        return (
            <div data-testid="blog-not-found" className="relative">
                <Navbar />
                <main>
                    <section
                        className="relative bg-white py-24 lg:py-32"
                    >
                        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 text-center">
                            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-communiti-text">
                                Article not found
                            </h1>
                            <p className="mt-4 text-communiti-muted">
                                The article you are looking for does not exist or has been removed.
                            </p>
                            <Link
                                to="/blog"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-communiti px-6 py-3 text-sm font-medium text-white hover:bg-communiti-soft transition-colors"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back to Articles
                            </Link>
                        </div>
                    </section>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div data-testid={`blog-post-${blog.id}`} className="relative">
            <Navbar />
            <main>
                <article
                    className="relative bg-white pt-20 sm:pt-24 lg:pt-28"
                >
            <style>{BLOG_STYLES}</style>

            <header className="relative bg-white border-b border-communiti-line">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
                    <div className="pt-6 pb-8 flex items-center gap-4">
                        <Link
                            to="/blog"
                            className="inline-flex items-center gap-2 text-sm text-communiti-muted hover:text-communiti transition-colors"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Articles
                        </Link>
                    </div>
                    <div className="max-w-3xl pb-12 lg:pb-20">
                        <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.18em] text-communiti-muted">
                            <span className="inline-flex items-center gap-1.5">
                                <span className="h-1.5 w-1.5 rounded-full bg-communiti" />
                                {blog.category}
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5" />
                                {formatDate(blog.date)}
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                {blog.read_time}
                            </span>
                        </div>
                        <h1 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-communiti-text leading-[0.95]">
                            {blog.title}
                        </h1>
                        <p className="mt-4 text-sm text-communiti-muted">
                            By <span className="text-communiti font-medium">{blog.author}</span>
                        </p>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
                <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden rounded-2xl -mt-4 mb-12 lg:mb-20">
                    <img
                        src={blog.featured_image}
                        alt={blog.title}
                        className="h-full w-full object-cover"
                    />
                </div>
            </div>

            <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-1">
                        <div className="hidden lg:flex flex-col items-center gap-6 text-xs text-communiti-muted sticky top-24">
                            <span className="uppercase tracking-[0.2em]">Share</span>
                            <div className="w-px h-8 bg-communiti-line" />
                        </div>
                    </div>
                    <div className="lg:col-span-7 xl:col-span-6">
                        {articleContent.toc.length > 0 && (
                            <nav
                                aria-label="Table of contents"
                                className="mb-10 rounded-2xl border border-communiti-line bg-communiti-surface/40 p-6 sm:p-8"
                            >
                                <p className="label-eyebrow">On this page</p>
                                <h2 className="mt-3 font-display text-xl font-bold tracking-tight text-communiti-text">
                                    Table of contents
                                </h2>
                                <ol className="mt-5 space-y-2.5">
                                    {articleContent.toc.map((item) => (
                                        <li
                                            key={item.id}
                                            className={
                                                item.level === 3 ? "pl-4" : ""
                                            }
                                        >
                                            <a
                                                href={`#${item.id}`}
                                                className="group flex items-start gap-3 text-sm leading-relaxed text-communiti-muted transition-colors hover:text-communiti"
                                            >
                                                <span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-communiti/50 transition-colors group-hover:bg-communiti" />
                                                <span>{item.label}</span>
                                            </a>
                                        </li>
                                    ))}
                                </ol>
                            </nav>
                        )}
                        <div
                            className="blog-content"
                            dangerouslySetInnerHTML={{ __html: articleContent.body }}
                        />
                        {articleContent.faqs.length > 0 && (
                            <FAQAccordion items={articleContent.faqs} />
                        )}

                        <div className="mt-12 pt-8 border-t border-communiti-line">
                            <Link
                                to="/blog"
                                className="inline-flex items-center gap-2 pt-4 text-sm font-medium text-communiti hover:text-communiti-soft transition-colors"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                All Articles
                            </Link>
                        </div>
                    </div>
                    <aside className="lg:col-span-4 xl:col-span-5">
                        <div className="lg:sticky lg:top-24 space-y-8">
                            <div className="rounded-2xl border border-communiti-line bg-communiti-surface/40 p-6 sm:p-8">
                                <h3 className="font-display text-lg font-bold tracking-tight text-communiti-text">
                                    About the author
                                </h3>
                                <p className="mt-3 text-sm text-communiti-muted leading-relaxed">
                                    Commmmuniti is a team of strategists, designers, and growth operators building brands that stand apart.
                                </p>
                                <Link
                                    to="/about"
                                    className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-communiti hover:text-communiti-soft transition-colors"
                                >
                                    Meet the team
                                    <ArrowUpRight className="h-3.5 w-3.5" />
                                </Link>
                            </div>

                            {relatedPosts.length > 0 && (
                                <div>
                                    <h3 className="font-display text-lg font-bold tracking-tight text-communiti-text mb-4">
                                        Related reading
                                    </h3>
                                    <div className="space-y-4">
                                        {relatedPosts.map((post) => (
                                            <Link
                                                key={post.id}
                                                to={`/blog/${post.id}`}
                                                className="group flex items-start gap-4 rounded-xl border border-communiti-line bg-white p-4 hover:border-communiti transition-colors"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <span className="text-[11px] uppercase tracking-[0.18em] text-communiti-muted">
                                                        {post.category}
                                                    </span>
                                                    <h4 className="mt-1 font-display text-base font-bold tracking-tight text-communiti-text leading-tight group-hover:text-communiti transition-colors">
                                                        {post.title}
                                                    </h4>
                                                    <span className="mt-2 inline-flex items-center gap-1 text-xs text-communiti-muted">
                                                        <ArrowUpRight className="h-3 w-3" />
                                                        Read article
                                                    </span>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <Link
                                to="/#contact"
                                className="flex items-center justify-center gap-2 rounded-full bg-communiti px-6 py-3.5 text-sm font-medium text-white hover:bg-communiti-soft transition-colors"
                            >
                                Start a project
                                <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </aside>
                </div>
            </div>
        </article>
            </main>
            <Footer />
        </div>
    );
}
